import config from '@/config';
import axiosInstance from '@/school-management/utils/api';
import { AxiosResponse } from 'axios';
import {
  TeacherGroupsResponse,
  TeacherCanBeDeletedResponse,
  DeleteTeacherWithActionsResponse,
  TeachersReportResponse,
  TeachersReportQueryParams,
} from '@/school-management/types/response';
import { TeacherDeleteGroupActionRequest } from '@/school-management/types/request/teacher';

const teachersReportInFlight = new Map<string, Promise<TeachersReportResponse>>();

export interface TeacherService {
  getAllTeachers: (
    organizationId: string,
    queryParams?: TeachersReportQueryParams
  ) => Promise<TeachersReportResponse>;
  getTeacherGroups: (organizationId: string, teacherId: string) => Promise<TeacherGroupsResponse>;
  updateTeacher: (
    organizationId: string,
    teacherId: string,
    payload: { firstName: string; lastName: string }
  ) => Promise<AxiosResponse<any>>;
  createTeachers: (
    organizationId: string,
    users: { userId?: string; firstName: string; lastName: string; email: string }[],
    dealId: string
  ) => Promise<AxiosResponse<any>>;
  checkIfTeacherCanBeDeleted: (
    schoolId: string,
    teacherId: string
  ) => Promise<TeacherCanBeDeletedResponse>;
  deleteTeacher: (schoolId: string, teacherId: string) => Promise<AxiosResponse<any>>;
  deleteTeacherWithActions(
    schoolId: string,
    teacherId: string,
    groupActions: TeacherDeleteGroupActionRequest[]
  ): Promise<DeleteTeacherWithActionsResponse>;
}

export const teacherService: TeacherService = {
  async getAllTeachers(
    organizationId: string,
    queryParams?: TeachersReportQueryParams
  ): Promise<TeachersReportResponse> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/teachers/report/search`;

    const body: Record<string, any> = {
      accountId: organizationId,
      page: queryParams?.page ?? 1,
      pageSize: queryParams?.pageSize ?? config.teachersPageSize,
    };

    // Sorting
    if (queryParams?.sortBy) body.sortBy = queryParams.sortBy;
    if (queryParams?.sortDirection) body.sortDirection = queryParams.sortDirection;

    if (queryParams?.search && queryParams.search.trim()) body.search = queryParams.search.trim();
    if (queryParams?.name && queryParams.name.trim()) body.name = queryParams.name.trim();
    if (queryParams?.teacherId) body.teacherId = queryParams.teacherId;
    if (queryParams?.groupId) body.groupId = queryParams.groupId;
    if (queryParams?.teacherIds && queryParams.teacherIds.length > 0)
      body.teacherIds = queryParams.teacherIds;

    if (queryParams?.loginTimes && Object.keys(queryParams.loginTimes).length > 0) {
      body.loginTimes = { ...queryParams.loginTimes };
    }

    const requestKey = `${url}:${JSON.stringify(body)}`;
    const existing = teachersReportInFlight.get(requestKey);
    if (existing) return existing;

    const promise = axiosInstance
      .post<TeachersReportResponse>(url, body)
      .then(res => res.data)
      .finally(() => {
        teachersReportInFlight.delete(requestKey);
      });
    teachersReportInFlight.set(requestKey, promise);
    return promise;
  },
  async getTeacherGroups(
    organizationId: string,
    teacherId: string
  ): Promise<TeacherGroupsResponse> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/groups/teacher/${teacherId}`;
    const { data } = await axiosInstance.get<TeacherGroupsResponse>(url);
    return data;
  },
  async updateTeacher(
    organizationId: string,
    teacherId: string,
    payload: { firstName: string; lastName: string }
  ): Promise<AxiosResponse<any>> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/teachers/${teacherId}`;
    return axiosInstance.post(url, payload);
  },
  async createTeachers(
    organizationId: string,
    users: { userId?: string; firstName: string; lastName: string; email: string }[],
    dealId: string
  ): Promise<AxiosResponse<any>> {
    const url = `/admin-management/${config.apiVersion}/${organizationId}/teachers/createTeachers`;
    return axiosInstance.post(url, {
      organizationId,
      users,
      dealId,
    });
  },
  async checkIfTeacherCanBeDeleted(
    schoolId: string,
    teacherId: string
  ): Promise<TeacherCanBeDeletedResponse> {
    const url = `/admin-management/${config.apiVersion}/${schoolId}/teachers/${teacherId}/can-be-deleted`;
    return axiosInstance.get(url);
  },
  async deleteTeacher(schoolId: string, teacherId: string): Promise<AxiosResponse<any>> {
    const url = `/admin-management/${config.apiVersion}/${schoolId}/teachers/${teacherId}`;
    return axiosInstance.delete(url);
  },
  async deleteTeacherWithActions(
    schoolId: string,
    teacherId: string,
    groupActions: TeacherDeleteGroupActionRequest[]
  ): Promise<DeleteTeacherWithActionsResponse> {
    const url = `/admin-management/${config.apiVersion}/${schoolId}/teachers/delete-with-actions`;
    return axiosInstance.post(url, {
      teacherId: teacherId,
      organizationId: schoolId,
      groupActions: groupActions,
    });
  },
};
